<script lang="ts" setup>
    import { reactive, ref, nextTick } from 'vue';
    import { DSIItem, DSIUserDismissItem, DSISearchItem, DSISearchInputs } from '../../lib/Models/DSIModel';
    import { ISearchPatientKey, ISearchPatientItem } from '../../lib/Models/SearchModel';
    import { ButtonDisplaySize, ButtonDisplayStyle } from '../../Enums/UiEnums';
    import { ICol } from "../../lib/ChoiceTableHelpers";
    import { popAlert } from '../../lib/Notification';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { isDateLessThanOrEqualTo} from '../../lib/DateUtils';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import Combo from "../../components/Combo.vue";
    import SearchPatient from '../../components/Search/SearchPatient.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const searchHeight = 180;
    const searchTable = ref<InstanceType<typeof ChoiceTable>>();
    const actionTakeList = [{ value: undefined, description: ""}, { value: 1, description: "Dismissed"}, { value: 0, description: "Re-Enabled"}];

    const props = defineProps<{
        supportList: DSIItem[],
        userList: DSIUserDismissItem[]
    }>();

    const formData = reactive<{
        startDate?: string,
        stopDate?: string,
        settingsId?: number,
        userId?: string,
        actionTaken?: number,
        defaultPatientKey?: ISearchPatientKey,
        windowHeight: number,
        searchList: DSISearchItem[],
        key: number,
        patientId?: number
    }>({
        windowHeight: (window.innerHeight - searchHeight),
        searchList: [],
        key: 0
    });

    const searchColumns = ref<ICol[]>([
        { Name: 'Chart #', DataField: 'patientId' },
        { Name: 'Patient', DataField: 'patientName' },
        { Name: 'DSI', DataField: 'description' },
        { Name: 'Feedback', DataField: 'feedback' },
        { Name: 'User', DataField: 'userName' },
        { Name: 'Date', DataField: 'dismissDate' },
        { Name: 'Location', DataField: 'location' },
        { Name: 'Action Taken', DataField: 'actionTaken' }
        ]);

    function windowResize() {
        formData.windowHeight = window.innerHeight - searchHeight;
    }

    async function onPatientSearch(searchResultItem: ISearchPatientItem) {
        if (!searchResultItem)
        {
            formData.patientId = undefined;
            return;
        }
        formData.patientId = searchResultItem.patient_id;
    }

    async function onSearch() {
        if (formData.startDate && formData.stopDate && isDateLessThanOrEqualTo(formData.startDate, formData.stopDate) === false) {
            await popAlert("The Start Date must be less than or equal to the Stop Date.", "Generate Report");
            return;
        }   

        formData.searchList = [];
        const searchInputs: DSISearchInputs = {
            startDate: formData.startDate,
            stopDate: formData.stopDate,
            settingsId: formData.settingsId,
            searchUserId: formData.userId,
            patientId: formData.patientId,
            actionTaken: formData.actionTaken
        };
        const result = await ChoiceApi.DSI.search(searchInputs);
        if (!result) {
            await popAlert("There was an error generating the report.  Please contact your System Administrator.", "Generate Report");
            return;
        }
        formData.searchList = result;
    }

    async function onClear() {
        formData.searchList = [];
        formData.startDate = undefined;
        formData.stopDate = undefined;  
        formData.settingsId = undefined;
        formData.userId = undefined;
        formData.defaultPatientKey = undefined;
        formData.actionTaken = undefined;
        formData.patientId = undefined;
        formData.key++;
    }

    async function onExport() {
        searchTable.value?.exportToExcel("DecisionSupportFeedback.xls", "DecisionSupportFeedback");
    }

</script>

<style>
    .dsi-date-width {
        width: 160px;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row mt-1 label-isalus-small">
        <div class="dsi-date-width">
            Date Range 
        </div>
        <div class="dsi-date-width">
        </div>
        <div class="col">
            DSI
        </div>
    </div>
    <div class="row mt-1">
        <div class="dsi-date-width">
            <DatePickerNoTZ 
                v-model:value="formData.startDate" 
                :clearable="false" 
                cssFramework="vanilla"
                :key="formData.key"/>
        </div>
        <div class="dsi-date-width">
            <DatePickerNoTZ 
                v-model:value="formData.stopDate" 
                :clearable="false" 
                cssFramework="vanilla"
                :key="formData.key"/>        
        </div>
        <div class="col">
            <Combo 
                :optionList="props.supportList"
                optionValue="settingsId"
                :includeBlank="true"
                v-model:selected="formData.settingsId" />
        </div>
    </div>
    <div class="row mt-2 label-isalus-small">
        <div class="col">
            User
        </div>
        <div class="col">
            Action Taken
        </div>
        <div class="col">
            Patient
        </div>
    </div>
    <div class="row">
        <div class="col">
            <Combo 
                :optionList="props.userList"
                optionValue="userId"
                optionDescription="userName"
                :includeBlank="true"
                v-model:selected="formData.userId" />
        </div>
        <div class="col">
            <Combo 
                :optionList="actionTakeList"
                optionValue="value"
                optionDescription="description"
                :includeBlank="false"
                v-model:selected="formData.actionTaken" />
        </div>
        <div class="col">
            <SearchPatient
                :defaultSearchKey="formData.defaultPatientKey" 
                :addToBaseClass="'label-isalus-small'"
                label=""
                @onChange="(val) => onPatientSearch(val)"
                :key="formData.key">                    
            </SearchPatient> 
        </div>
    </div>
    <div class="row mt-2">
        <div class="col-auto">
            <ActionButtonV2 
                :action="ActionButtonStyle.Search"
                text="Generate Report"
                spinText="Generating"
                tooltip="Generate the report based on the input criteria."
                :size="ButtonDisplaySize.Small" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onSearch"/>   
        </div>
        <div class="col-auto ps-0">
            <ActionButtonV2 
                :action="ActionButtonStyle.Erase"
                text="Clear"
                spinText="Clearing"
                tooltip="Clear the screen and start over."
                :size="ButtonDisplaySize.Small" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onClear"/>   
        </div>
        <div class="col-auto ps-0">
            <ActionButtonV2 
                :action="ActionButtonStyle.Excel"
                text="Export"
                spinText="Exporting"
                tooltip="Export the results to Excel."
                :size="ButtonDisplaySize.Small" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onExport"/>   
        </div>
    </div>
    <div class="row mt-3">
        <div class="col">
            <ChoiceTable 
                ref="searchTable"
                :columns="searchColumns" 
                :data="formData.searchList" 
                :show-filters="false" 
                :allowSorting="false"
                :height="formData.windowHeight"
                fontSize="smaller"
                :key-value-data-member="'recordId'" />
        </div>
    </div>
</template>

